import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Search, X, Calendar as CalendarIcon, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { TaskFilterState } from '@/hooks/useTaskFilters';
import { Project } from '@/hooks/useProjects';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface Contact {
  id: string;
  name: string;
}

interface TaskFilterBarProps {
  filters: TaskFilterState;
  onFilterChange: (filters: Partial<TaskFilterState>) => void;
  onClearFilters: () => void;
  activeFilterCount: number;
  projects: Project[];
  contacts: Contact[];
  availableTags: string[];
}

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'stuck', label: 'Stuck' },
  { value: 'done', label: 'Done' },
  { value: 'archived', label: 'Archived' },
];

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export function TaskFilterBar({
  filters,
  onFilterChange,
  onClearFilters,
  activeFilterCount,
  projects,
  contacts,
  availableTags,
}: TaskFilterBarProps) {
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout>();
  const [isOpen, setIsOpen] = useState(activeFilterCount > 0);

  useEffect(() => {
    if (activeFilterCount > 0 && !isOpen) {
      setIsOpen(true);
    }
  }, [activeFilterCount, isOpen]);

  const handleSearchChange = (value: string) => {
    if (searchDebounce) clearTimeout(searchDebounce);
    const timeout = setTimeout(() => {
      onFilterChange({ search: value });
    }, 300);
    setSearchDebounce(timeout);
  };

  const toggleArrayFilter = (key: keyof TaskFilterState, value: string) => {
    const currentArray = filters[key] as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(v => v !== value)
      : [...currentArray, value];
    onFilterChange({ [key]: newArray });
  };

  const removeFilter = (key: keyof TaskFilterState, value?: string) => {
    if (key === 'search') {
      onFilterChange({ search: '' });
    } else if (key === 'dueDateFrom' || key === 'dueDateTo') {
      onFilterChange({ [key]: null });
    } else if (key === 'showAICreatedOnly' || key === 'showWithEmailsOnly' || key === 'showOverdueOnly') {
      onFilterChange({ [key]: false });
    } else if (value) {
      const currentArray = filters[key] as string[];
      onFilterChange({ [key]: currentArray.filter(v => v !== value) });
    }
  };

  const getFilterLabel = (key: string, value: string) => {
    switch (key) {
      case 'statuses':
        return STATUS_OPTIONS.find(s => s.value === value)?.label || value;
      case 'priorities':
        return PRIORITY_OPTIONS.find(p => p.value === value)?.label || value;
      case 'projectIds':
        if (value === 'no_project') return 'No Project';
        return projects.find(p => p.id === value)?.name || value;
      case 'assignedToContactIds':
        if (value === 'unassigned') return 'Unassigned';
        return contacts.find(c => c.id === value)?.name || value;
      default:
        return value;
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="overflow-hidden">
        {/* Collapsible Header */}
        <div className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 border-b">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 hover:bg-transparent p-0">
              <Filter className="h-4 w-4" />
              <span className="font-medium text-sm sm:text-base">Filters</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {activeFilterCount}
                </Badge>
              )}
              {isOpen ? (
                <ChevronUp className="h-4 w-4 ml-2" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-2" />
              )}
            </Button>
          </CollapsibleTrigger>
          
          {/* Compact filter summary when collapsed */}
          {!isOpen && activeFilterCount > 0 && (
            <div className="flex-1 flex items-center gap-2 overflow-hidden w-full sm:w-auto">
              <div className="flex flex-wrap gap-1 overflow-hidden">
                {filters.search && (
                  <Badge variant="outline" className="text-xs">
                    Search: {filters.search.slice(0, 20)}{filters.search.length > 20 ? '...' : ''}
                  </Badge>
                )}
                {filters.statuses.slice(0, 2).map(status => (
                  <Badge key={status} variant="outline" className="text-xs">
                    {getFilterLabel('statuses', status)}
                  </Badge>
                ))}
                {filters.priorities.slice(0, 2).map(priority => (
                  <Badge key={priority} variant="outline" className="text-xs">
                    {getFilterLabel('priorities', priority)}
                  </Badge>
                ))}
                {activeFilterCount > 5 && (
                  <Badge variant="outline" className="text-xs">
                    +{activeFilterCount - 5} more
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          {/* Clear All button */}
          {activeFilterCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClearFilters}
              className="shrink-0 w-full sm:w-auto"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>

        {/* Collapsible Content */}
        <CollapsibleContent>
          <div className="p-4 space-y-4">
            {/* Main Filters Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            defaultValue={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Status Multi-Select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-between">
              <span className="truncate">
                {filters.statuses.length > 0 ? `Status (${filters.statuses.length})` : 'Status'}
              </span>
              <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-1">
              {STATUS_OPTIONS.map(option => (
                <div
                  key={option.value}
                  className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                  onClick={() => toggleArrayFilter('statuses', option.value)}
                >
                  <div className={cn("w-4 h-4 border rounded flex items-center justify-center", 
                    filters.statuses.includes(option.value) && "bg-primary border-primary")}>
                    {filters.statuses.includes(option.value) && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
                    )}
                  </div>
                  <span className="text-sm">{option.label}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Priority Multi-Select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-between">
              <span className="truncate">
                {filters.priorities.length > 0 ? `Priority (${filters.priorities.length})` : 'Priority'}
              </span>
              <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-1">
              {PRIORITY_OPTIONS.map(option => (
                <div
                  key={option.value}
                  className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                  onClick={() => toggleArrayFilter('priorities', option.value)}
                >
                  <div className={cn("w-4 h-4 border rounded flex items-center justify-center", 
                    filters.priorities.includes(option.value) && "bg-primary border-primary")}>
                    {filters.priorities.includes(option.value) && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
                    )}
                  </div>
                  <span className="text-sm">{option.label}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Project Multi-Select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-between">
              <span className="truncate">
                {filters.projectIds.length > 0 ? `Project (${filters.projectIds.length})` : 'Project'}
              </span>
              <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2 max-h-80 overflow-y-auto" align="start">
            <div className="space-y-1">
              <div
                className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                onClick={() => toggleArrayFilter('projectIds', 'no_project')}
              >
                <div className={cn("w-4 h-4 border rounded flex items-center justify-center", 
                  filters.projectIds.includes('no_project') && "bg-primary border-primary")}>
                  {filters.projectIds.includes('no_project') && (
                    <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
                  )}
                </div>
                <span className="text-sm">No Project</span>
              </div>
              {projects.map(project => (
                <div
                  key={project.id}
                  className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                  onClick={() => toggleArrayFilter('projectIds', project.id)}
                >
                  <div className={cn("w-4 h-4 border rounded flex items-center justify-center", 
                    filters.projectIds.includes(project.id) && "bg-primary border-primary")}>
                    {filters.projectIds.includes(project.id) && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
                    )}
                  </div>
                  <span className="text-sm truncate">{project.name}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Tags Multi-Select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-between">
              <span className="truncate">
                {filters.tags.length > 0 ? `Tags (${filters.tags.length})` : 'Tags'}
              </span>
              <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2 max-h-80 overflow-y-auto" align="start">
            {availableTags.length === 0 ? (
              <p className="text-sm text-muted-foreground p-2">No tags available</p>
            ) : (
              <div className="space-y-1">
                {availableTags.map(tag => (
                  <div
                    key={tag}
                    className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                    onClick={() => toggleArrayFilter('tags', tag)}
                  >
                    <div className={cn("w-4 h-4 border rounded flex items-center justify-center", 
                      filters.tags.includes(tag) && "bg-primary border-primary")}>
                      {filters.tags.includes(tag) && (
                        <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
                      )}
                    </div>
                    <span className="text-sm truncate">{tag}</span>
                  </div>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

            {/* Second Row - Date Range & Assigned To */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Due Date Range */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-start">
              <CalendarIcon className="h-4 w-4 mr-2" />
              {filters.dueDateFrom || filters.dueDateTo ? (
                <span className="truncate">
                  {filters.dueDateFrom && format(filters.dueDateFrom, 'MMM d')}
                  {filters.dueDateFrom && filters.dueDateTo && ' - '}
                  {filters.dueDateTo && format(filters.dueDateTo, 'MMM d')}
                </span>
              ) : (
                'Due Date Range'
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-3 space-y-3">
              {/* Quick Preset Buttons */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Quick Select</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      onFilterChange({ dueDateFrom: today, dueDateTo: today });
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const startOfWeek = new Date(today);
                      startOfWeek.setDate(today.getDate() - today.getDay());
                      const endOfWeek = new Date(startOfWeek);
                      endOfWeek.setDate(startOfWeek.getDate() + 6);
                      onFilterChange({ dueDateFrom: startOfWeek, dueDateTo: endOfWeek });
                    }}
                  >
                    This Week
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      const today = new Date();
                      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                      onFilterChange({ dueDateFrom: startOfMonth, dueDateTo: endOfMonth });
                    }}
                  >
                    This Month
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const nextWeek = new Date(today);
                      nextWeek.setDate(today.getDate() + 7);
                      onFilterChange({ dueDateFrom: today, dueDateTo: nextWeek });
                    }}
                  >
                    Next 7 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs col-span-2"
                    onClick={() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const next30 = new Date(today);
                      next30.setDate(today.getDate() + 30);
                      onFilterChange({ dueDateFrom: today, dueDateTo: next30 });
                    }}
                  >
                    Next 30 Days
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Custom Range Calendar */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Custom Range</Label>
                <Calendar
                  mode="range"
                  selected={{
                    from: filters.dueDateFrom || undefined,
                    to: filters.dueDateTo || undefined,
                  }}
                  onSelect={(range) => {
                    onFilterChange({
                      dueDateFrom: range?.from || null,
                      dueDateTo: range?.to || null,
                    });
                  }}
                  numberOfMonths={1}
                  className="pointer-events-auto"
                />
              </div>

              {/* Selected Range Display & Actions */}
              {(filters.dueDateFrom || filters.dueDateTo) && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">
                      Selected: {filters.dueDateFrom && format(filters.dueDateFrom, 'MMM d, yyyy')}
                      {filters.dueDateFrom && filters.dueDateTo && ' - '}
                      {filters.dueDateTo && format(filters.dueDateTo, 'MMM d, yyyy')}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full h-8"
                      onClick={() => onFilterChange({ dueDateFrom: null, dueDateTo: null })}
                    >
                      Clear Range
                    </Button>
                  </div>
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Assigned To Multi-Select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-between">
              <span className="truncate">
                {filters.assignedToContactIds.length > 0 ? `Assigned (${filters.assignedToContactIds.length})` : 'Assigned To'}
              </span>
              <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2 max-h-80 overflow-y-auto" align="start">
            <div className="space-y-1">
              <div
                className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                onClick={() => toggleArrayFilter('assignedToContactIds', 'unassigned')}
              >
                <div className={cn("w-4 h-4 border rounded flex items-center justify-center", 
                  filters.assignedToContactIds.includes('unassigned') && "bg-primary border-primary")}>
                  {filters.assignedToContactIds.includes('unassigned') && (
                    <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
                  )}
                </div>
                <span className="text-sm">Unassigned</span>
              </div>
              {contacts.map(contact => (
                <div
                  key={contact.id}
                  className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                  onClick={() => toggleArrayFilter('assignedToContactIds', contact.id)}
                >
                  <div className={cn("w-4 h-4 border rounded flex items-center justify-center", 
                    filters.assignedToContactIds.includes(contact.id) && "bg-primary border-primary")}>
                    {filters.assignedToContactIds.includes(contact.id) && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-sm" />
                    )}
                  </div>
                  <span className="text-sm truncate">{contact.name}</span>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

            </div>

            {/* Quick Toggles */}
            <div className="flex flex-wrap gap-4 pt-2 border-t">
        <div className="flex items-center gap-2">
          <Switch
            checked={filters.showAICreatedOnly}
            onCheckedChange={(checked) => onFilterChange({ showAICreatedOnly: checked })}
            id="ai-created"
          />
          <Label htmlFor="ai-created" className="text-sm cursor-pointer">AI-created only</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={filters.showWithEmailsOnly}
            onCheckedChange={(checked) => onFilterChange({ showWithEmailsOnly: checked })}
            id="with-emails"
          />
          <Label htmlFor="with-emails" className="text-sm cursor-pointer">Has linked emails</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={filters.showOverdueOnly}
            onCheckedChange={(checked) => onFilterChange({ showOverdueOnly: checked })}
            id="overdue"
          />
          <Label htmlFor="overdue" className="text-sm cursor-pointer">Overdue only</Label>
        </div>
            </div>

            {/* Active Filters Badges */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
          {filters.search && (
            <Badge variant="secondary" className="gap-1">
              Search: {filters.search}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('search')} />
            </Badge>
          )}
          {filters.statuses.map(status => (
            <Badge key={status} variant="secondary" className="gap-1">
              {getFilterLabel('statuses', status)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('statuses', status)} />
            </Badge>
          ))}
          {filters.priorities.map(priority => (
            <Badge key={priority} variant="secondary" className="gap-1">
              {getFilterLabel('priorities', priority)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('priorities', priority)} />
            </Badge>
          ))}
          {filters.projectIds.map(projectId => (
            <Badge key={projectId} variant="secondary" className="gap-1">
              {getFilterLabel('projectIds', projectId)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('projectIds', projectId)} />
            </Badge>
          ))}
          {filters.tags.map(tag => (
            <Badge key={tag} variant="secondary" className="gap-1">
              Tag: {tag}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('tags', tag)} />
            </Badge>
          ))}
          {(filters.dueDateFrom || filters.dueDateTo) && (
            <Badge variant="secondary" className="gap-1">
              Due: {filters.dueDateFrom && format(filters.dueDateFrom, 'MMM d')}
              {filters.dueDateFrom && filters.dueDateTo && ' - '}
              {filters.dueDateTo && format(filters.dueDateTo, 'MMM d')}
              <X className="h-3 w-3 cursor-pointer" onClick={() => onFilterChange({ dueDateFrom: null, dueDateTo: null })} />
            </Badge>
          )}
          {filters.assignedToContactIds.map(contactId => (
            <Badge key={contactId} variant="secondary" className="gap-1">
              {getFilterLabel('assignedToContactIds', contactId)}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter('assignedToContactIds', contactId)} />
            </Badge>
          ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
